import { TrashIcon } from "@heroicons/react/24/outline";
import { formatPrice } from "utils/convertNumbThousand";

const Invoice = ({
  invoice,
  carName,
  promoSlot,
  onRemoveCoupon,
}: {
  invoice: any;
  carName: any;
  promoSlot?: React.ReactNode;
  onRemoveCoupon?: () => void;
}) => {
  const couponRateText =
    invoice?.coupon_info?.coupon_type === "percentage"
      ? `${invoice?.coupon_info?.amount || "0"}% off`
      : ["fixed", "amount"].includes(invoice?.coupon_info?.coupon_type)
        ? `$${formatPrice(invoice?.coupon_info?.amount || "0")} off`
        : null;

  const couponDeductionValue = Number(
    invoice?.coupon_discount ?? invoice?.discount ?? 0,
  );

  // Render the coupon row whenever a coupon is attached, even when the
  // resolved discount is 0
  const hasCouponDeduction =
    Boolean(invoice?.coupon_info?.name) && couponDeductionValue >= 0;

  // Reverse-derive the base the coupon was applied to so the user can see
  // what's actually being discounted
  const couponAmountPct = Number(invoice?.coupon_info?.amount ?? 0);
  const couponBase =
    invoice?.coupon_info?.coupon_type === "percentage" && couponAmountPct > 0
      ? couponDeductionValue / (couponAmountPct / 100)
      : null;
  const couponApplicableOn = Array.isArray(invoice?.coupon_info?.applicable_on)
    ? invoice?.coupon_info?.applicable_on
    : invoice?.coupon_info?.applicable_on?.array;
  const couponIncludesDeposit =
    Array.isArray(couponApplicableOn) &&
    couponApplicableOn.includes("Deposit Total");

  // Show the days/auto discount row even when a coupon is applied —
  // the two are separate concessions and both belong on the invoice
  const hasRegularDiscount =
    invoice?.discount !== undefined &&
    Number(invoice?.discount || 0.0) > 0;

  const carSubtotal = Number(invoice?.car_total || 0);
  const insuranceSubtotal = Number(invoice?.insurance_total || 0);
  const extrasSubtotal = Number(invoice?.extras_total || 0);
  const subtotal = carSubtotal + insuranceSubtotal + extrasSubtotal;

  const hasInsurance =
    invoice?.breakdown?.insurance?.length > 0 &&
    invoice?.insurance_total !== undefined;

  const hasExtras =
    invoice?.breakdown?.extras?.length > 0 &&
    invoice?.extras_total !== undefined;

  const hasLocations =
    invoice?.breakdown?.locations?.length > 0 &&
    invoice?.location_total !== undefined;

  const bookingFeeFromBreakdown = invoice?.breakdown?.booking_fee?.reduce(
    (sum: number, row: any) => sum + Number(row?.total || 0),
    0,
  );
  const bookingFeeAmount =
    invoice?.booking_fee !== undefined && invoice?.booking_fee !== null
      ? Number(invoice?.booking_fee)
      : bookingFeeFromBreakdown;
  const hasBookingFee =
    (invoice?.breakdown?.booking_fee?.length > 0) ||
    (invoice?.booking_fee !== undefined && invoice?.booking_fee !== null);

  const hasTax =
    invoice?.breakdown?.taxes?.length > 0 && invoice?.tax !== undefined;

  const finalTotal = invoice?.coupon_info
    ? invoice?.total_price_after_coupon_without_security
    : invoice?.total_price_without_security;

  const tripDays = invoice?.trip_days || 0;

  const joinNames = (rows: any[] | undefined) =>
    rows
      ?.map((r: any) => r?.description || r?.name)
      .filter(Boolean)
      .join(" • ");

  const formatRateSubtitle = (rate: number | string | undefined | null) => {
    if (rate === undefined || rate === null || rate === "") {
      return `$${formatPrice(0)} x Trip`;
    }
    const formatted = formatPrice(rate as number | string);
    const hasUnit = /[a-zA-Z]/.test(String(rate)) || /[a-zA-Z]/.test(formatted);
    return hasUnit ? `$${formatted}` : `$${formatted} x Trip`;
  };

  const discountRate = invoice?.breakdown?.discount?.rate;
  const discountRateLabel = discountRate
    ? typeof discountRate === "string" && discountRate.includes("%")
      ? discountRate
      : `$${formatPrice(discountRate)} off`
    : undefined;

  const paymentStatus =
    invoice?.payment_status ?? invoice?.booking?.payment_status;
  const isPaid =
    paymentStatus === "paid" ||
    invoice?.is_paid === true ||
    invoice?.paid === true;
  const rawPaymentMethod =
    invoice?.payment_method ??
    invoice?.payment_type ??
    invoice?.paid_with ??
    invoice?.booking?.payment_method;
  const paymentLastFour =
    invoice?.payment_last_four ??
    invoice?.payment_method_last_four ??
    invoice?.card_last_four;
  const paymentBrand =
    invoice?.payment_brand ??
    invoice?.card_brand ??
    invoice?.payment_method_brand;
  const paymentLabel = formatPaymentLabel(
    rawPaymentMethod,
    paymentBrand,
    paymentLastFour,
  );
  const showPaidWith = Boolean(isPaid && paymentLabel);

  return (
    <div className="mt-4 rounded-2xl bg-bg-color p-6 sm:p-7 border border-secondary-100/70 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <h3 className="text-xl font-bold text-text-color mb-6 tracking-tight">
        Summary
      </h3>

      <div className="space-y-5">
        <LineItem
          name={carName}
          subtitle={`$${formatPrice(invoice?.avg_car_price_per_day) || "0.00"} x ${tripDays}-${tripDays === 1 ? "Day" : "Days"}`}
          amount={`$${formatPrice(invoice?.car_total)}`}
        />

        {hasInsurance &&
          invoice?.breakdown?.insurance?.map((insurance: any, index: number) => (
            <LineItem
              key={`ins-${index}`}
              quantity={
                Number(insurance?.quantity) > 1
                  ? `${insurance.quantity}x`
                  : undefined
              }
              name={insurance?.name}
              subtitle={formatRateSubtitle(insurance?.rate)}
              amount={`$${formatPrice(insurance?.total || 0)}`}
            />
          ))}

        {hasExtras &&
          invoice?.breakdown?.extras?.map((extra: any, index: number) => (
            <LineItem
              key={`ext-${index}`}
              quantity={
                Number(extra?.quantity) > 1 ? `${extra.quantity}x` : undefined
              }
              name={extra?.name}
              subtitle={formatRateSubtitle(extra?.rate)}
              amount={`$${formatPrice(extra?.total || 0)}`}
            />
          ))}
      </div>

      <div className="my-6 border-t border-secondary-100" />

      <div className="space-y-3">
        <SummaryRow label="Subtotal" amount={`$${formatPrice(subtotal)}`} />

        {hasRegularDiscount && (
          <div className="flex justify-between items-start">
            <div className="min-w-0 pr-3">
              <span className="text-text-color">Discount</span>
              {discountRateLabel && (
                <p className="text-xs text-text-color/60 mt-0.5">
                  {discountRateLabel}
                </p>
              )}
            </div>
            <span className="inline-flex items-center rounded-full py-1 text-sm font-bold text-red-600 tabular-nums whitespace-nowrap">
              -${formatPrice(invoice?.discount)}
            </span>
          </div>
        )}

        {hasCouponDeduction && (
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0 pr-3">
              <span className="text-text-color font-medium">
                {invoice?.coupon_info?.name}
              </span>
              {couponRateText && (
                <p className="text-xs text-text-color/60 mt-0.5">
                  {couponRateText}
                </p>
              )}
              {couponBase !== null && couponBase > 0 && (
                <p className="text-xs text-text-color/60 mt-0.5">
                  Applied to ${formatPrice(couponBase)}
                  {couponIncludesDeposit ? " (incl. deposit)" : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm font-semibold text-red-600 tabular-nums whitespace-nowrap">
                -${formatPrice(couponDeductionValue)}
              </span>
              {onRemoveCoupon && (
                <button
                  type="button"
                  onClick={onRemoveCoupon}
                  aria-label="Remove coupon"
                  title="Remove coupon"
                  className="inline-flex items-center justify-center rounded-md bg-red-100 p-1.5 text-red-700 hover:bg-red-200 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {hasBookingFee && (
          <SummaryRow
            label="Service fee"
            sublabel={joinNames(invoice?.breakdown?.booking_fee)}
            amount={`$${formatPrice(bookingFeeAmount || 0)}`}
          />
        )}

        {hasTax && (
          <SummaryRow
            label="Tax"
            sublabel={joinNames(invoice?.breakdown?.taxes)}
            amount={`$${formatPrice(invoice?.tax)}`}
          />
        )}

        {hasLocations && (
          <SummaryRow
            label="Location fee"
            sublabel={joinNames(invoice?.breakdown?.locations)}
            amount={`$${formatPrice(invoice?.location_total)}`}
          />
        )}
      </div>

      {promoSlot && <div className="mt-5">{promoSlot}</div>}

      <div className="my-6 border-t border-secondary-100" />

      {finalTotal !== "0.00" && finalTotal !== undefined && finalTotal !== null && (
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold text-text-color">
            Total{hasTax ? " (incl. tax)" : ""}
          </span>
          <span className="text-2xl font-extrabold tabular-nums text-text-color">
            ${formatPrice(finalTotal)}
          </span>
        </div>
      )}

      {Number(invoice?.security_deposit_total) > 0 && (() => {
        // When a coupon reduced the deposit, the customer will only be charged
        // security_deposit_after_coupon. Surface that as the headline number
        // and strike through the pre-coupon amount so the math is auditable.
        const depositBefore = Number(invoice?.security_deposit_total || 0);
        const depositAfter =
          invoice?.security_deposit_after_coupon !== undefined &&
          invoice?.security_deposit_after_coupon !== null
            ? Number(invoice?.security_deposit_after_coupon)
            : depositBefore;
        const showStrike =
          hasCouponDeduction &&
          Math.abs(depositBefore - depositAfter) > 0.005;
        return (
          <div className="mt-5 flex justify-between items-center">
            <div className="min-w-0 pr-3">
              <span className="text-lg font-bold text-text-color">
                Deposit
              </span>
              <p className="text-xs text-text-color/60 mt-0.5">
                Refundable · held during trip · returned after completion
              </p>
            </div>
            <div className="flex items-baseline gap-2 whitespace-nowrap">
              {showStrike && (
                <span className="text-sm tabular-nums text-text-color/50 line-through">
                  ${formatPrice(depositBefore)}
                </span>
              )}
              <span className="text-2xl font-extrabold tabular-nums text-text-color">
                ${formatPrice(depositAfter)}
              </span>
            </div>
          </div>
        );
      })()}

      {showPaidWith && (
        <div className="mt-6 pt-5 border-t border-secondary-100">
          <p className="text-sm font-bold text-text-color mb-3">Paid with</p>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 min-w-0 pr-3">
              <span
                aria-hidden
                className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-secondary-100 bg-bg-color/60 text-text-color"
              >
                <BankIcon />
              </span>
              <span className="text-sm font-medium text-text-color truncate">
                {paymentLabel}
              </span>
            </div>
            <span className="text-sm font-semibold tabular-nums text-text-color whitespace-nowrap">
              ${formatPrice(finalTotal)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const formatPaymentLabel = (
  method: string | undefined | null,
  brand?: string | null,
  lastFour?: string | null,
): string | null => {
  if (!method) return null;
  const m = String(method).toLowerCase();
  const friendly =
    m === "stripe" || m === "card" || m === "credit_card"
      ? "Card"
      : m === "payment_link" || m === "link"
        ? "Payment link"
        : m === "manual" || m === "cash" || m === "manual_payment"
          ? "Manual payment"
          : m === "square"
            ? "Square"
            : m === "paypal"
              ? "PayPal"
              : m === "online" || m === "online_payment"
                ? "Online payment"
                : String(method).replace(/_/g, " ");
  const brandLabel = brand ? String(brand).replace(/_/g, " ") : "";
  const tail = lastFour ? ` ending in ${lastFour}` : "";
  if (brandLabel && tail) return `${brandLabel}${tail}`;
  if (brandLabel) return `${friendly} · ${brandLabel}`;
  return `${friendly}${tail}`;
};

const BankIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 10h18" />
    <path d="M5 10v8" />
    <path d="M19 10v8" />
    <path d="M9 10v8" />
    <path d="M15 10v8" />
    <path d="M3 18h18" />
    <path d="M12 3 2 8h20Z" />
  </svg>
);

const LineItem = ({
  quantity,
  name,
  subtitle,
  amount,
}: {
  quantity?: string;
  name: string;
  subtitle?: string;
  amount: string;
}) => (
  <div className="flex items-start gap-3">
    {quantity && (
      <span className="text-text-color/70 font-medium min-w-[28px] pt-0.5 tabular-nums">
        {quantity}
      </span>
    )}
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-text-color truncate">{name}</p>
      {subtitle && (
        <p className="text-sm text-text-color/60 mt-0.5">{subtitle}</p>
      )}
    </div>
    <span className="font-semibold text-text-color tabular-nums whitespace-nowrap">
      {amount}
    </span>
  </div>
);

const SummaryRow = ({
  label,
  sublabel,
  amount,
}: {
  label: string;
  sublabel?: string;
  amount: string;
}) => (
  <div className="flex justify-between items-start">
    <div className="min-w-0 pr-3">
      <span className="text-text-color">{label}</span>
      {sublabel && (
        <p className="text-xs text-text-color/60 mt-0.5 truncate">{sublabel}</p>
      )}
    </div>
    <span className="text-text-color tabular-nums whitespace-nowrap">
      {amount}
    </span>
  </div>
);

export default Invoice;
